<template>
  <div>
    <el-card shadow="hover">
      <template slot="header">
        <div class="clearfix">
          <span>商品管理</span>
          <div style="float: right">
            <el-button type="success" size="small" @click="exportCsv">导出CSV</el-button>
            <el-button v-if="$hasPerm('func:product:create')" type="primary" size="small" @click="openDialog()">新建商品</el-button>
          </div>
        </div>
      </template>

      <!-- 筛选 -->
      <el-form :inline="true" :model="searchForm" class="mb-4">
        <el-form-item label="商品名称">
          <el-input v-model="searchForm.name" placeholder="请输入" style="width: 180px" @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="商品分类">
          <el-select v-model="searchForm.categoryId" placeholder="全部" clearable style="width: 160px" @change="handleSearch">
            <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="试用类型">
          <el-select v-model="searchForm.applicableTypeId" placeholder="全部" clearable style="width: 160px" @change="handleSearch">
            <el-option v-for="t in applicableTypeList" :key="t.id" :label="t.name" :value="t.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="礼品类型">
          <el-select v-model="searchForm.giftTypeId" placeholder="全部" clearable style="width: 160px" @change="handleSearch">
            <el-option v-for="gt in giftTypeList" :key="gt.id" :label="gt.name" :value="gt.id">
              <span style="float: left">{{ gt.name }}</span>
              <span
                v-if="gt.color"
                :style="'float: right; width: 12px; height: 12px; margin-top: 11px; border-radius: 2px; background: ' + gt.color"
              ></span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">筛选</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 列表 -->
      <el-table :data="goodsList" stripe size="small" border @row-dblclick="row => openDialog(row)">
        <el-table-column type="expand" width="40" fixed="left">
          <template slot-scope="props">
            <div class="detail-panel dbl-edit" @dblclick="openDialog(props.row)" title="双击任意位置修改">
              <div class="detail-panel-title"><i class="el-icon-info"></i><span>商品详情</span></div>
              <div class="detail-card-row">
                <div class="detail-card-body cols-4">
                  <div class="dc-cell"><span class="dc-label">商品ID</span><span class="dc-value">{{ props.row.id }}</span></div>
                  <div class="dc-cell"><span class="dc-label">商品名称</span><span class="dc-value">{{ props.row.name || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">商品分类</span>
                    <el-tag :type="isGiftCategory(props.row.categoryId) ? 'warning' : 'primary'" size="mini">{{ getCategoryName(props.row.categoryId) }}</el-tag>
                  </div>
                  <div class="dc-cell"><span class="dc-label">规格</span><span class="dc-value">{{ props.row.spec || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">价格</span><span class="dc-value">¥{{ props.row.price || 0 }}</span></div>
                  <div class="dc-cell"><span class="dc-label">排序值</span><span class="dc-value">{{ props.row.sortOrder != null ? props.row.sortOrder : 0 }}</span></div>
                  <div class="dc-cell" v-if="!isGiftCategory(props.row.categoryId)"><span class="dc-label">禁售上限</span><span class="dc-value">{{ props.row.stockLimit != null ? props.row.stockLimit : 0 }}</span></div>
                  <div class="dc-cell"><span class="dc-label">预警阈值</span>
                    <el-tag type="warning" size="mini">{{ props.row.warningThreshold != null ? props.row.warningThreshold : 100 }}</el-tag>
                  </div>
                  <div class="dc-cell"><span class="dc-label">库存</span>
                    <el-tag :type="lowStock(props.row) ? 'danger' : 'success'" size="mini">{{ props.row.stock || 0 }}</el-tag>
                  </div>
                  <div class="dc-cell"><span class="dc-label">已累计入库</span><span class="dc-value">{{ props.row.inboundCount || 0 }}</span></div>
                  <div class="dc-cell"><span class="dc-label">已累计出库</span><span class="dc-value">{{ props.row.outboundCount || 0 }}</span></div>
                  <div class="dc-cell"><span class="dc-label">创建时间</span><span class="dc-value">{{ formatDate(props.row.createTime) || '-' }}</span></div>
                  <div class="dc-cell w4" v-if="!isGiftCategory(props.row.categoryId)">
                    <span class="dc-label">试用类型与独立次数</span>
                    <div v-if="props.row.applicableTypeList && props.row.applicableTypeList.length" style="display:inline-flex; gap:6px; flex-wrap:wrap">
                      <el-tag
                        v-for="t in props.row.applicableTypeList"
                        :key="t.applicableTypeId"
                        :color="t.applicableTypeColor || ''"
                        :style="t.applicableTypeColor ? 'color:#fff;border-color:' + t.applicableTypeColor : ''"
                        size="mini"
                      >
                        {{ t.applicableTypeName }}（{{ t.applicableCount ? t.applicableCount + '次' : '不限次数' }}）
                      </el-tag>
                    </div>
                    <span v-else class="dc-value">-</span>
                  </div>
                  <div class="dc-cell w4" v-else>
                    <span class="dc-label">礼品类型</span>
                    <div v-if="props.row.giftTypeList && props.row.giftTypeList.length" style="display:inline-flex; gap:6px; flex-wrap:wrap">
                      <el-tag
                        v-for="g in props.row.giftTypeList"
                        :key="g.giftTypeId"
                        :color="g.giftTypeColor || '#E6A23C'"
                        :style="g.giftTypeColor ? 'color:#fff;border-color:' + g.giftTypeColor : 'color:#fff;border-color:#E6A23C'"
                        size="mini"
                      >
                        {{ g.giftTypeName }}
                      </el-tag>
                    </div>
                    <span v-else class="dc-value">-</span>
                  </div>
                  <div class="dc-cell w4"><span class="dc-label">详情</span><span class="dc-value">{{ props.row.description || '-' }}</span></div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="商品名称" min-width="150" fixed="left"></el-table-column>
        <el-table-column label="商品分类" width="90" align="center">
          <template slot-scope="s">
            <el-tag :type="isGiftCategory(s.row.categoryId) ? 'warning' : 'primary'" size="mini">
              {{ getCategoryName(s.row.categoryId) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="80" align="center" sortable>
          <template slot-scope="s">
            <el-tag size="mini" type="info">{{ s.row.sortOrder != null ? s.row.sortOrder : 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="spec" label="规格" width="80" show-overflow-tooltip>
          <template slot-scope="s">{{ s.row.spec || '-' }}</template>
        </el-table-column>
        <el-table-column label="价格" width="100">
          <template slot-scope="s">¥{{ s.row.price || 0 }}</template>
        </el-table-column>
        <el-table-column label="禁售上限" width="100" align="center">
          <template slot-scope="s">
            <span v-if="isGiftCategory(s.row.categoryId)" style="color:#909399">-</span>
            <span v-else>{{ s.row.stockLimit != null ? s.row.stockLimit : 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="预警阈值" width="100" align="center">
          <template slot-scope="s">
            <el-tag type="warning" size="mini">{{ s.row.warningThreshold != null ? s.row.warningThreshold : 100 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="试用 / 礼品类型" min-width="180">
          <template slot-scope="s">
            <template v-if="!isGiftCategory(s.row.categoryId)">
              <div v-if="s.row.applicableTypeList && s.row.applicableTypeList.length" style="display:flex; flex-wrap:wrap; gap:4px">
                <el-tag
                  v-for="t in s.row.applicableTypeList"
                  :key="t.applicableTypeId"
                  :color="t.applicableTypeColor || ''"
                  :style="t.applicableTypeColor ? 'color:#fff;border-color:' + t.applicableTypeColor : ''"
                  size="mini"
                >
                  {{ t.applicableTypeName }}: {{ t.applicableCount ? t.applicableCount + '次' : '不限' }}
                </el-tag>
              </div>
              <el-tag
                v-else-if="s.row.applicableTypeName"
                :color="s.row.applicableTypeColor || ''"
                :style="s.row.applicableTypeColor ? 'color:#fff;border-color:' + s.row.applicableTypeColor : ''"
                size="mini"
              >
                {{ s.row.applicableTypeName }}: {{ s.row.applicableCount ? s.row.applicableCount + '次' : '不限' }}
              </el-tag>
              <span v-else style="color:#909399">-</span>
            </template>
            <template v-else>
              <div v-if="s.row.giftTypeList && s.row.giftTypeList.length" style="display:flex; flex-wrap:wrap; gap:4px">
                <el-tag
                  v-for="g in s.row.giftTypeList"
                  :key="g.giftTypeId"
                  :color="g.giftTypeColor || '#E6A23C'"
                  :style="(g.giftTypeColor ? 'color:#fff;border-color:' + g.giftTypeColor : 'color:#fff;border-color:#E6A23C')"
                  size="mini"
                >
                  {{ g.giftTypeName }}
                </el-tag>
              </div>
              <span v-else style="color:#909399">-</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="库存" width="100" align="center">
          <template slot-scope="s">
            <el-tag :type="lowStock(s.row) ? 'danger' : 'success'" size="mini">{{ s.row.stock || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="已累计入库" width="110" align="center">
          <template slot-scope="s">{{ s.row.inboundCount || 0 }}</template>
        </el-table-column>
        <el-table-column label="已累计出库" width="110" align="center">
          <template slot-scope="s">{{ s.row.outboundCount || 0 }}</template>
        </el-table-column>
        <el-table-column prop="description" label="详情" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="id" label="ID" width="70"></el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="s">
            <el-button v-if="$hasPerm('func:product:edit')" size="mini" type="primary" @click="openDialog(s.row)">修改</el-button>
            <el-button v-if="$hasPerm('func:product:delete')" size="mini" type="danger" @click="handleDelete(s.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-4">
        <el-pagination
          @size-change="s => { pageSize = s; loadList() }"
          @current-change="p => { currentPage = p; loadList() }"
          :current-page="currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper" :total="total"></el-pagination>
      </div>
    </el-card>

    <!-- 新建/修改对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="880px" top="5vh" :close-on-click-modal="false" :close-on-press-escape="false">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px" size="small">
        <el-row :gutter="20">
          <el-col :span="12"><el-form-item label="商品分类" prop="categoryId">
            <el-select v-model="form.categoryId" placeholder="选择商品分类" style="width: 100%" @change="onCategoryChange">
              <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id"></el-option>
            </el-select>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="名称" prop="name">
            <el-input v-model="form.name" :placeholder="isGiftCategory(form.categoryId) ? '礼品名称' : '产品名称'"></el-input>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12"><el-form-item label="规格">
            <el-input v-model="form.spec" placeholder="如 500g/瓶、24件/箱" maxlength="100"></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="价格" prop="price">
            <el-input-number v-model="form.price" :min="0" :precision="2" controls-position="right" style="width: 100%"></el-input-number>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12" v-if="!isGiftCategory(form.categoryId)"><el-form-item label="禁售上限">
            <el-input-number v-model="form.stockLimit" :min="0" controls-position="right" style="width: 100%" placeholder="每个客户最多可存数量"></el-input-number>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="预警阈值">
            <el-input-number v-model="form.warningThreshold" :min="0" controls-position="right" style="width: 100%" placeholder="不填默认 100"></el-input-number>
            <span class="muted small" style="display:block;color:#909399;font-size:12px;line-height:1.4;margin-top:4px">库存低于该值进入个人待办「库存不足」；不填默认 100</span>
          </el-form-item></el-col>
          <el-col :span="12" v-if="isGiftCategory(form.categoryId)"><el-form-item label="排序值" prop="sortOrder">
            <el-input-number v-model="form.sortOrder" :min="0" :step="1" controls-position="right" style="width: 100%" placeholder="数字越小越靠前"></el-input-number>
            <span class="muted small" style="display:block;color:#909399;font-size:12px;line-height:1.4;margin-top:4px">数字越小越靠前（默认0，在小程序滚轮选择时排在前面）</span>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="20" v-if="!isGiftCategory(form.categoryId)">
          <el-col :span="12"><el-form-item label="排序值" prop="sortOrder">
            <el-input-number v-model="form.sortOrder" :min="0" :step="1" controls-position="right" style="width: 100%" placeholder="数字越小越靠前"></el-input-number>
            <span class="muted small" style="display:block;color:#909399;font-size:12px;line-height:1.4;margin-top:4px">数字越小越靠前（默认0，在小程序滚轮选择时排在前面）</span>
          </el-form-item></el-col>
        </el-row>

        <!-- 多试用类型动态配置模块（常规产品展示） -->
        <div v-if="!isGiftCategory(form.categoryId)" class="trial-section-box">
          <div class="trial-section-header">
            <span class="section-title"><i class="el-icon-connection"></i> 试用类型与独立次数设置</span>
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="addTrialTypeRow">添加试用类型</el-button>
          </div>
          <div v-if="!form.applicableTypeList || !form.applicableTypeList.length" class="trial-empty-tip">
            <i class="el-icon-warning-outline"></i> 暂未配置试用类型（该产品将不支持试用申请；若需支持，请点击右上角「添加试用类型」）
          </div>
          <div v-else class="trial-row-container">
            <div v-for="(item, idx) in form.applicableTypeList" :key="idx" class="trial-row-item">
              <span class="row-index">#{{ idx + 1 }}</span>
              <div class="trial-field">
                <span class="field-label">试用类型：</span>
                <el-select
                  v-model="item.applicableTypeId"
                  placeholder="请选择试用类型"
                  size="small"
                  style="width: 220px"
                  @change="onTrialTypeSelect(item)"
                >
                  <el-option
                    v-for="t in applicableTypeList"
                    :key="t.id"
                    :label="t.name"
                    :value="t.id"
                    :disabled="isTypeSelected(t.id, idx)"
                  >
                    <span style="float: left">{{ t.name }}</span>
                    <span v-if="t.color" :style="'float: right; width: 14px; height: 14px; margin-top: 10px; border-radius: 2px; background: ' + t.color"></span>
                  </el-option>
                </el-select>
              </div>
              <div class="trial-field">
                <span class="field-label">试用次数限制：</span>
                <el-input-number
                  v-model="item.applicableCount"
                  :min="0"
                  :step="1"
                  controls-position="right"
                  size="small"
                  placeholder="0或空为不限"
                  style="width: 130px"
                ></el-input-number>
                <span class="field-hint">次（0或留空表示不限制）</span>
              </div>
              <el-button
                type="danger"
                icon="el-icon-delete"
                circle
                size="mini"
                @click="removeTrialTypeRow(idx)"
                title="删除该试用类型"
              ></el-button>
            </div>
          </div>
        </div>

        <!-- 礼品类型多选动态配置模块（商品分类选为礼品时展示） -->
        <div v-else class="trial-section-box gift-type-section-box">
          <div class="trial-section-header">
            <span class="section-title"><i class="el-icon-collection-tag"></i> 礼品类型设置（可多选）</span>
          </div>
          <div style="padding: 10px 4px 6px;">
            <el-form-item label="礼品类型" prop="giftTypeIds" label-width="90px" style="margin-bottom: 0;">
              <el-select
                v-model="form.giftTypeIds"
                multiple
                clearable
                placeholder="请选择礼品类型（支持同时勾选多个类型标签）"
                style="width: 100%"
              >
                <el-option
                  v-for="t in giftTypeList"
                  :key="t.id"
                  :label="t.name"
                  :value="t.id"
                >
                  <span style="float: left">{{ t.name }}</span>
                  <span
                    v-if="t.color"
                    :style="'float: right; width: 14px; height: 14px; margin-top: 10px; border-radius: 2px; background: ' + t.color"
                  ></span>
                </el-option>
              </el-select>
              <div class="muted small" style="color:#909399;font-size:12px;line-height:1.5;margin-top:6px">
                支持多对多打标：一个礼品可以同时勾选多个类型标签（例如某款高档茶礼既属于【大使礼品】，也可以作为【直播兑换】）
              </div>
            </el-form-item>
          </div>
        </div>

        <el-row :gutter="20" v-if="form.id" style="margin-top: 12px">
          <el-col :span="8"><el-form-item label="当前库存">
            <el-input :value="form.stock || 0" disabled></el-input>
          </el-form-item></el-col>
          <el-col :span="8"><el-form-item label="已累计入库">
            <el-input :value="form.inboundCount || 0" disabled></el-input>
          </el-form-item></el-col>
          <el-col :span="8"><el-form-item label="已累计出库">
            <el-input :value="form.outboundCount || 0" disabled></el-input>
          </el-form-item></el-col>
        </el-row>
        <el-form-item label="详情">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="商品描述"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="success" @click="handleSubmit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ProductManagement',
  data() {
    return {
      searchForm: { name: '', categoryId: null, applicableTypeId: null, giftTypeId: null },
      goodsList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      dialogVisible: false,
      form: this.emptyForm(),
      categoryList: [],
      applicableTypeList: [],
      giftTypeList: [],
      rules: {
        categoryId: [{ required: true, message: '请选择商品分类', trigger: 'change' }],
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        price: [{ required: true, message: '请输入价格', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      const action = this.form.id ? '修改' : '新建'
      const kind = this.isGiftCategory(this.form.categoryId) ? '礼品' : '产品'
      return action + kind
    }
  },
  mounted() {
    this.loadCategories()
    this.loadApplicableTypes()
    this.loadGiftTypes()
    this.loadList()
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: '',
        code: '',
        categoryId: null,
        applicableTypeList: [],
        giftTypeIds: [],
        price: 0,
        sortOrder: 0,
        stock: 0,
        stockLimit: 0,
        warningThreshold: null,
        spec: '',
        description: ''
      }
    },
    isGiftCategory(id) {
      const c = this.categoryList.find(x => x.id === id)
      return !!(c && ['Gift', '礼品'].includes(c.name))
    },
    getCategoryName(id) {
      const c = this.categoryList.find(x => x.id === id)
      return c ? c.name : (id || '—')
    },
    loadCategories() {
      axios.get('/category/list').then(res => { this.categoryList = res.data || [] })
    },
    loadApplicableTypes() {
      axios.get('/applicable-type/list?enabled=1').then(res => { this.applicableTypeList = res.data || [] })
    },
    loadGiftTypes() {
      axios.get('/gift-type/list?enabled=1').then(res => {
        this.giftTypeList = Array.isArray(res.data) ? res.data : []
      }).catch(() => {})
    },
    loadList() {
      // 用 /product/page 一次拉所有商品（含礼品），includeGift=true
      const params = { page: this.currentPage, size: this.pageSize, includeGift: true }
      if (this.searchForm.name) params.name = this.searchForm.name
      if (this.searchForm.categoryId) params.categoryId = this.searchForm.categoryId
      if (this.searchForm.applicableTypeId) params.applicableTypeId = this.searchForm.applicableTypeId
      if (this.searchForm.giftTypeId) params.giftTypeId = this.searchForm.giftTypeId
      axios.get('/product/page', { params }).then(res => {
        this.goodsList = (res.data && res.data.records) || []
        this.total = (res.data && res.data.total) || 0
      })
    },
    handleSearch() { this.currentPage = 1; this.loadList() },
    resetSearch() {
      this.searchForm = { name: '', categoryId: null, applicableTypeId: null, giftTypeId: null }
      this.handleSearch()
    },

    openDialog(row) {
      this.loadCategories()
      this.loadApplicableTypes()
      this.loadGiftTypes()
      if (row) {
        let list = []
        if (row.applicableTypeList && row.applicableTypeList.length) {
          list = JSON.parse(JSON.stringify(row.applicableTypeList))
        } else if (row.applicableTypeId) {
          list = [{ applicableTypeId: row.applicableTypeId, applicableCount: row.applicableCount || 0 }]
        }
        let giftTypeIds = []
        if (row.giftTypeIds && row.giftTypeIds.length) {
          giftTypeIds = [...row.giftTypeIds]
        } else if (row.giftTypeList && row.giftTypeList.length) {
          giftTypeIds = row.giftTypeList.map(x => x.giftTypeId)
        }
        this.form = {
          ...row,
          stockLimit: this.isGiftCategory(row.categoryId) ? 0 : (row.stockLimit != null ? row.stockLimit : 0),
          sortOrder: row.sortOrder != null ? row.sortOrder : 0,
          applicableTypeList: list,
          giftTypeIds: giftTypeIds
        }
      } else {
        this.form = this.emptyForm()
      }
      this.dialogVisible = true
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
    },
    onCategoryChange(catId) {
      if (this.isGiftCategory(catId)) {
        this.form.applicableTypeList = []
        this.form.stockLimit = 0
        if (!this.form.giftTypeIds) {
          this.$set(this.form, 'giftTypeIds', [])
        }
      } else {
        this.form.giftTypeIds = []
      }
    },
    addTrialTypeRow() {
      if (!this.form.applicableTypeList) {
        this.$set(this.form, 'applicableTypeList', [])
      }
      const selectedIds = this.form.applicableTypeList.map(x => x.applicableTypeId)
      const available = this.applicableTypeList.find(t => !selectedIds.includes(t.id))
      const newItem = {
        applicableTypeId: available ? available.id : null,
        applicableCount: 0,
        applicableTypeName: available ? available.name : '',
        applicableTypeColor: available ? available.color : ''
      }
      this.form.applicableTypeList.push(newItem)
    },
    removeTrialTypeRow(idx) {
      this.form.applicableTypeList.splice(idx, 1)
    },
    isTypeSelected(typeId, currentIdx) {
      if (!this.form.applicableTypeList) return false
      return this.form.applicableTypeList.some((item, idx) => idx !== currentIdx && item.applicableTypeId === typeId)
    },
    onTrialTypeSelect(item) {
      const found = this.applicableTypeList.find(t => t.id === item.applicableTypeId)
      if (found) {
        item.applicableTypeName = found.name
        item.applicableTypeColor = found.color
      }
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (this.isGiftCategory(this.form.categoryId)) {
          this.form.applicableTypeList = []
          this.form.stockLimit = 0
          this.form.giftTypeIds = this.form.giftTypeIds || []
        } else {
          this.form.giftTypeIds = []
          if (this.form.applicableTypeList) {
            this.form.applicableTypeList = this.form.applicableTypeList.filter(t => t.applicableTypeId != null)
          }
        }
        const path = this.isGiftCategory(this.form.categoryId) ? '/gift' : '/product'
        const req = this.form.id ? axios.put(path, this.form) : axios.post(path, this.form)
        req.then(res => {
          if (res.data) {
            this.$message.success(this.form.id ? '修改成功' : '新建成功')
            this.dialogVisible = false
            this.loadList()
          } else {
            this.$message.error('操作失败')
          }
        })
      })
    },
    handleDelete(row) {
      const isGift = this.isGiftCategory(row.categoryId)
      const kind = isGift ? '礼品' : '产品'
      this.$confirm('确定删除该' + kind + '？', '提示', { type: 'warning' }).then(() => {
        const path = (isGift ? '/gift/' : '/product/') + row.id
        axios.delete(path).then(res => {
          if (res.data) { this.$message.success('删除成功'); this.loadList() }
        })
      }).catch(() => {})
    },
    lowStock(row) {
      if (this.isGiftCategory(row.categoryId)) {
        const threshold = row.warningThreshold != null ? row.warningThreshold : 100
        return (row.stock || 0) < threshold
      }
      return (row.stock || 0) < (row.stockLimit || 0)
    },
    formatDate(v) { return v ? String(v).replace('T', ' ').slice(0, 10) : '-' },
    exportCsv() {
      const cols = ['ID', '商品名称', '商品分类', '排序', '规格', '价格', '禁售上限', '预警阈值', '试用/礼品类型', '库存', '已累计入库', '已累计出库', '详情', '创建时间']
      const rows = this.goodsList.map(o => {
        let typeStr = '-'
        if (this.isGiftCategory(o.categoryId)) {
          if (o.giftTypeList && o.giftTypeList.length) {
            typeStr = o.giftTypeList.map(g => g.giftTypeName).join('; ')
          } else if (o.giftTypeNames) {
            typeStr = o.giftTypeNames
          }
        } else {
          if (o.applicableTypeList && o.applicableTypeList.length) {
            typeStr = o.applicableTypeList.map(t => `${t.applicableTypeName || '试用'}(${t.applicableCount ? t.applicableCount + '次' : '不限'})`).join('; ')
          } else if (o.applicableTypeName) {
            typeStr = `${o.applicableTypeName}(${o.applicableCount ? o.applicableCount + '次' : '不限'})`
          }
        }
        return [
          o.id, o.name || '',
          this.getCategoryName(o.categoryId),
          o.sortOrder != null ? o.sortOrder : 0,
          o.spec || '',
          o.price || 0,
          this.isGiftCategory(o.categoryId) ? '-' : (o.stockLimit != null ? o.stockLimit : 0),
          o.warningThreshold != null ? o.warningThreshold : 100,
          typeStr,
          o.stock || 0,
          o.inboundCount || 0,
          o.outboundCount || 0,
          o.description || '',
          this.formatDate(o.createTime)
        ].map(v => v == null ? '' : String(v).replace(/,/g, '，'))
      })
      const csv = '\uFEFF' + [cols.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '商品_' + new Date().toISOString().slice(0, 10) + '.csv'
      link.click()
    }
  }
}
</script>

<style scoped>
.mb-4 { margin-bottom: 16px; }
.mt-4 { margin-top: 16px; }

.detail-panel { padding: 14px 24px 18px; background: linear-gradient(180deg,#fafcff 0%,#f5f7fa 100%); border-top: 1px dashed #dcdfe6; }
.detail-panel-title { display: flex; align-items: center; gap: 6px; color: #303133; font-size: 13px; font-weight: 600; margin: 4px 0 12px; }
.detail-panel-title i { color: #409eff; }
.detail-card-row { background: #fff; border: 1px solid #ebeef5; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 12px 14px; margin-bottom: 12px; }
.detail-card-body { display: grid; gap: 8px; }
.detail-card-body.cols-4 { grid-template-columns: repeat(4, 1fr); }
.dc-cell { display: flex; justify-content: flex-start; align-items: center; gap: 8px; padding: 6px 10px; background: #f8f9fb; border-radius: 4px; }
.dc-cell.w4 { grid-column: span 4; }
.dc-label { color: #909399; font-size: 12px; flex-shrink: 0; }
.dc-label::after { content: '：'; }
.dc-value { color: #303133; font-size: 13px; font-weight: 500; }
.dbl-edit { cursor: pointer; transition: background .15s; }
.dbl-edit:hover { background: #f0f7ff; }

/* 试用类型配置区域样式 */
.trial-section-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 10px 0 16px 20px;
}
.trial-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.section-title i {
  color: #409eff;
  margin-right: 4px;
}
.trial-empty-tip {
  color: #94a3b8;
  font-size: 12px;
  padding: 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.trial-row-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.trial-row-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 8px 12px;
}
.row-index {
  color: #94a3b8;
  font-size: 12px;
  font-weight: bold;
}
.trial-field {
  display: flex;
  align-items: center;
  gap: 6px;
}
.field-label {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}
.field-hint {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}
</style>
